import { Injectable, inject } from "@angular/core";
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { ConstantVariables } from "./constant-variables";
import { CourseInstanceConstants } from "@app/pages/course/course-util/course-instance-constants";
import { InstitutionConstants } from "@app/pages/institution/utils/institution-constants";
import { AvailabilityConstants } from "@app/pages/availability/availability-util/availability-constants";

@Injectable({
  providedIn: 'root'
})
export class ValidatorFunctions {
  constantVariable = inject(ConstantVariables);
  courseInstanceConstants = inject(CourseInstanceConstants);
  institutionConstants = inject(InstitutionConstants);
  availabilityConstants = inject(AvailabilityConstants);
  ajaxValidator(valueCheck?: any, length?: any): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      let inputValue = (typeof control.value !== 'string') && control.value != null ? control.value[valueCheck] : control.value;
      inputValue = inputValue?.trim();
      let errors: any = null;
      if (inputValue)
        if (inputValue?.length < (length ?? 3))
          errors = { 'minLength': true }
        else if (!control.value[valueCheck])
          errors = { 'notFound': true }
      return errors;
    }
  }
  /**
   * @see: This function is used to validate campus detail fields
  */
  dataValidator(attrVal: any): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (attrVal?.attributeId)
        attrVal.fieldId = String(attrVal.attributeId)
      const inputVal = (control.value && typeof control.value === 'string' && control.value?.trim().length > 0) ? control.value.trim() : null;
      let errors: any = null;
      if (inputVal) {
        const charValidation = attrVal?.characterValidation;
        if ((attrVal?.minLength && inputVal.length < attrVal?.minLength) || (attrVal?.maxLength && inputVal.length > attrVal?.maxLength))
          errors = { 'minMaxLength': true }
        else if (charValidation || ['stage', String(this.institutionConstants.institutionAttributeIds.bcPostCode), String(this.availabilityConstants.availabilityAttrIds.bcDurationAttrId), String(this.availabilityConstants.availabilityAttrIds.bcPostcodeAttrId)].includes(attrVal.fieldId))
          errors = this.commonCharValidation(charValidation, inputVal, attrVal.fieldId);
        errors = this.callValidation(attrVal, inputVal, errors);
      }
      return errors;
    }
  }
  callValidation(attrVal: any, inputVal: any, errors: any) {
    if (attrVal.fieldId?.toLowerCase().includes('infolink') || attrVal.fieldId?.toLowerCase().includes('url') || this.constantVariable.urlFieldAttributeIds.includes(String(attrVal.attributeId)))
      errors = this.urlValidation(inputVal)
    if (!errors && (attrVal.fieldId?.toLowerCase().includes('email') || (attrVal.fieldId === String(this.institutionConstants.institutionAttributeIds.domainEmail) && attrVal.attributeName === this.institutionConstants.domainEmailAttrName)))
      errors = this.emailValidation(inputVal);
    const validateFields = ["campusName", "city", "displayCity", "state", "district", "courseTitle", "courseInstanceTitle", "courseDuration", "moduleName", "registeredInstitutionName", "institutionDisplayName", "institutionAliasName", String(this.courseInstanceConstants.instanceAttributeIds.bcCourseInstanceTitle), "displayName", "aliasName"];
    if (!errors && validateFields.includes(attrVal.fieldId))
      errors = this.alphaNumricValidation(inputVal);
    if (!errors && attrVal.fieldId === 'durationUnit')
      errors = this.decimalFieldValidation(inputVal);
    if (!errors && attrVal.fieldId === 'geoCode')
      errors = this.geoCodeFieldValidation(inputVal);
    return errors;
  }
  /**
   * @see: This function is used to validate characters
  */
  commonCharValidation(charValidation: any, inputVal: any, fieldId: any) {
    let errors = null;
    if (['geoCode', 'unitNo', 'stage', 'ranking']?.includes(fieldId) || fieldId?.toLowerCase()?.includes('telephonenumber')) {
      errors = this.numericCharacterValidation(charValidation, inputVal, fieldId);
    } else if ([String(this.institutionConstants.institutionAttributeIds.bcPostCode), String(this.availabilityConstants.availabilityAttrIds.bcPostcodeAttrId)].includes(fieldId)) {
      const regExp = new RegExp(this.constantVariable.alphaNumericPattern);
      if (!regExp.test(inputVal))
        errors = { 'charNotAllowed': true };
    } else if (charValidation && charValidation?.includes('exact')) {
      errors = this.exactMatchValidator(charValidation, inputVal, fieldId);
    } else if (fieldId === String(this.availabilityConstants.availabilityAttrIds.bcDurationAttrId)) {
      const characterValidation = "^[\\w\\d " + charValidation + "]*$";
      const regexVal: RegExp = new RegExp(characterValidation);
      if (!regexVal.test(inputVal)) {
        errors = { 'charNotAllowed': true }
      } else if (Number(inputVal) === 0) {
        errors = { 'onlyZeroNotAllowed': true }
      } else if (Number(inputVal)) {
        const regexVal: RegExp = new RegExp('^\\d+(\\.\\d+)?$');
        if (!regexVal.test(inputVal))
          errors = { 'charNotValid': true };
      }
    } else {
      const characterValidation = "^[\\w\\d" + charValidation + "]*$";
      const regexVal: RegExp = new RegExp(characterValidation);
      if (charValidation?.includes("except"))
        errors = this.specialCharacterValidation(charValidation, inputVal);
      else if (!regexVal.test(inputVal))
        errors = { 'charNotAllowed': true }
    }
    return errors;
  }

  /**
   * @see: This function is used to validate if input matches `charValidation` regex
  */
  exactMatchValidator(charValidation: any, inputVal: any, fieldId: any) {
    const regex: RegExp = new RegExp(charValidation.replace('exact', '')?.trim());
    if (!regex.test(inputVal))
      if (fieldId == this.constantVariable.domTelAttributeId)
        return { 'invalidTelNumber': true }
      else if (fieldId == this.constantVariable.indicativeFeeUGAttributeId || fieldId == this.constantVariable.indicativeFeePGAttributeId)
        return { 'invalidIndicativeFee': true }
      else
        return { 'charNotAllowed': true }
    return null;
  }

  /**
   * @see: This function is used to validate only number not allowed
  */
  alphaNumricValidation(inputVal: any) {
    let errors = null;
    if (this.constantVariable.onlyNumberPattern.test(inputVal))
      errors = { 'onlyNumberNotAllowed': true }
    return errors;
  }
  /**
   * @see: This function is used to validate geocode field character
  */
  numericCharacterValidation(charValidation: any, inputVal: any, fieldId: any) {
    const numberValidation = "^[\\d" + charValidation + "]*$";
    let errors = null;
    const regexVal: RegExp = new RegExp(numberValidation)
    if (fieldId === 'unitNo' && !regexVal.test(inputVal))
      errors = { 'alphabetsNotAllowed': true }
    else if (!regexVal.test(inputVal) && (fieldId !== 'stage'))
      errors = { 'alphabeticCharNotAllowed': true }
    else if (['unitNo', 'stage', 'ranking'].includes(fieldId) && (Number(inputVal) === 0))
      errors = { 'onlyZeroNotAllowed': true }
    return errors;
  }

  decimalFieldValidation(inputVal: any) {
    let errors;
    const regexVal: RegExp = new RegExp(this.constantVariable.numericValidation);
    if (!regexVal.test(inputVal))
      errors = { 'charNotAllowed': true };
    else if (Number(inputVal) === 0)
      errors = { 'onlyZeroNotAllowed': true };
    return errors;
  }
  /**
   * @see: This function is used to validate special characters for input fields
  */
  specialCharacterValidation(expChar: any, inputVal: any,) {
    const exceptionalChars = "^[" + this.addEscapeChar(expChar) + "]*$";
    const exceptCharExp: RegExp = new RegExp(exceptionalChars);
    let errors = null;
    if (!exceptCharExp.test(inputVal))
      errors = { 'charNotAllowed': true }
    return errors;
  }
  /**
   * @see: This function is used to add escape character
  */
  addEscapeChar(pattern: string) {
    let changedPattern = pattern;
    const charList = ["\\", "[", "]", "except"];
    charList?.forEach((char: string) => {
      if (pattern?.includes(char))
        changedPattern = changedPattern.replace(char, ((char === "except") ? "^" : ("\\" + char)));
    })
    return changedPattern;
  }
  /**
   * @see: This function is used to validate url
  */
  urlValidation(inputVal: any) {
    let errors = null;
    try {
      if (inputVal?.indexOf("?") != -1)
        inputVal = inputVal?.substring(0, inputVal?.indexOf("?"));
      const regExp = new RegExp(this.constantVariable.urlPattern);
      if (!regExp.test(inputVal))
        errors = { 'invalidUrl': true }
    } catch (error) {
      console.error(error)
    }
    return errors;
  }
  /**
   * @see: This function is used to check if field is empty
  */
  isFieldEmpty() {
    return (control: AbstractControl) => {
      const value = control.value
      let errors = null;
      if (value && typeof value === 'string' && value.trim()?.length <= 0)
        errors = { 'required': true };
      return errors;
    };
  }

  setValidatorFunction(validationMsg: any) {
    return (): { [key: string]: any } => {
      return { 'existError': validationMsg }
    }
  }
  /**
   * @see: This function is used to validate email
  */
  emailValidation(email: any) {
    let errors = null;
    const regex = this.constantVariable.emailPattern;
    if (!regex.test(email.trim()))
      errors = { 'emailerror': true }
    return errors;
  }

  addValidators(field: any) {
    const fieldValidator: any = [];
    if (field.mandatory === 'Y') {
      fieldValidator.push(Validators.required);
      if (['TEXT_BOX', 'AJAX'].includes(field.fieldType))
        fieldValidator.push(this.isFieldEmpty());
    }
    if (['TEXT_BOX', 'TEXT_AREA', 'CK_EDITOR']?.includes(field.fieldType))
      fieldValidator.push(this.dataValidator(field));
    return fieldValidator;
  }

  geoCodeFieldValidation(inputVal: any) {
    let errors = null;
    if (inputVal) {
      const val = inputVal.split(',');
      if (val.length !== 2 || !val[0] || !val[1] || !Number(val[0]) || !Number(val[1])) {
        errors = { 'geoCodeError': true };
      } else if (val.length === 2) {
        const latitude = val[0]?.replaceAll(".", '')?.replaceAll("-", '');
        const longitude = val[1]?.replaceAll(".", '')?.replaceAll("-", '');
        if (latitude?.length > 12 || longitude?.length > 13)
          errors = { 'geoCodeError': true };
      }
    }
    return errors;
  }

  rangeValidator(fieldId?: any, beforeDecimal?: number, afterDecimal?: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      let error: any = null;
      const value = control.value?.trim() ?? '';
      if (this.constantVariable.avalNumericFields.includes(fieldId) && value && Number(value) === 0)
        error = { 'onlyZeroNotAllowed': true }
      else if ((value.split('.')?.length > 2 || value?.split('.')[0]?.length > (beforeDecimal || 2) || value?.split('.')[1]?.length > (afterDecimal || 2)))
        error = { "rangeError": true };
      return error;
    }
  }

  componentScoreValidators(componentDetails: any): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      let errors: any = null;
      const value = control?.value ?? '';
      const decimalPattern = /^\d+(\.\d{1,})?$/;
      if (value && (!decimalPattern.test(value) || (Number(value) < Number(componentDetails.minValue) || Number(value) > Number(componentDetails.maxValue))))
        errors = { "rangeError": true };
      return errors;
    }
  }
}
